// Starter view — the A1 "First words" practice. A calm, stepped flip-through of
// the starter phrases (Portuguese front, tap to reveal English, with audio), then a
// "Mark complete" step. Reached from the First Words article's "Practice these
// phrases" link. No scores; the only persisted thing is completion.
import { STARTER_PHRASES } from "../data/starterPhrases.js";
import { speakButtonHTML } from "../lib/audio.js";
import { escapeHtml } from "../ui/helpers.js";
import { isAuthed } from "../lib/auth.js";
import { openAuthPanel } from "../ui/authPanel.js";
import { markStarterDone } from "../lib/starter.js";
import { navigate } from "../router/router.js";

let st = null;

export function starterView(app) {
  st = { idx: 0, flipped: false };
  render(app);
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function render(app) {
  const total = STARTER_PHRASES.length;
  const c = STARTER_PHRASES[st.idx];
  const atEnd = st.idx >= total - 1;

  app.innerHTML = `
    <section class="max-w-xl mx-auto fade-in">
      <button id="st-back" class="text-sm font-medium text-sage-600 hover:text-sage-800 mb-4 inline-flex items-center gap-1">← First Words</button>
      <h2 class="text-2xl font-bold text-sage-800">First words · Practice</h2>
      <p class="text-sage-600 text-sm mt-1 mb-4">Tap the card to reveal the English. Go through all ${total}, then mark it complete.</p>
      <p class="text-xs text-sage-400 mb-2 tabular-nums">${st.idx + 1} / ${total}</p>

      <button id="st-card" class="w-full bg-white border border-sage-200 rounded-2xl p-8 min-h-[180px] flex flex-col items-center justify-center text-center hover:border-sage-300 transition">
        <p class="text-2xl font-bold text-sage-800 flex items-center gap-2">${escapeHtml(c.pt)}${speakButtonHTML(c.pt, { size: "lg" })}</p>
        ${
          st.flipped
            ? `<p class="mt-3 text-lg text-sage-600">${escapeHtml(c.en)}</p>`
            : `<p class="mt-3 text-sm text-sage-400">tap to reveal</p>`
        }
      </button>

      <div class="flex items-center gap-2 mt-4">
        <button id="st-prev" class="px-4 py-2 rounded-lg border border-sage-200 text-sage-600 text-sm font-medium hover:border-sage-300 disabled:opacity-40" ${st.idx === 0 ? "disabled" : ""}>← Prev</button>
        <button id="st-next" class="ml-auto bg-sage-500 hover:bg-sage-600 text-white px-5 py-2 rounded-lg text-sm font-medium">${atEnd ? "Finish ✓" : "Next →"}</button>
      </div>

      <div id="st-done" class="mt-6"></div>
    </section>`;

  app.querySelector("#st-back").addEventListener("click", () => navigate("article", { slug: "a1-first-words" }));
  app.querySelector("#st-card").addEventListener("click", () => {
    st.flipped = !st.flipped;
    render(app);
  });
  app.querySelector("#st-prev").addEventListener("click", () => {
    if (st.idx > 0) {
      st.idx--;
      st.flipped = false;
      render(app);
    }
  });
  app.querySelector("#st-next").addEventListener("click", () => {
    if (!atEnd) {
      st.idx++;
      st.flipped = false;
      render(app);
    } else {
      renderDone(app);
    }
  });
}

function renderDone(app) {
  const slot = app.querySelector("#st-done");
  if (!slot) return;
  slot.innerHTML = `
    <div class="bg-gradient-to-b from-sage-50 to-white border border-sage-200 rounded-2xl p-6 text-center">
      <p class="text-2xl mb-1">🎉</p>
      <p class="text-sage-700 font-medium">That's your first words — nicely done.</p>
      <p class="text-sage-500 text-sm mt-1 mb-4">Mark it complete to clear it from your A1 start, then move on whenever you're ready.</p>
      <button id="st-complete" class="bg-sage-500 hover:bg-sage-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium">Mark as complete</button>
      <p id="st-msg" class="text-xs text-sage-400 mt-2"></p>
    </div>`;
  slot.scrollIntoView({ behavior: "smooth", block: "center" });

  slot.querySelector("#st-complete").addEventListener("click", async () => {
    if (!isAuthed()) {
      openAuthPanel("signin");
      return;
    }
    const btn = slot.querySelector("#st-complete");
    const msg = slot.querySelector("#st-msg");
    btn.disabled = true;
    btn.textContent = "Saving…";
    const r = await markStarterDone();
    if (r && r.ok) {
      slot.innerHTML = `
        <div class="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 text-center">
          <p class="text-2xl mb-1">✓</p>
          <p class="text-emerald-800 font-medium">First words complete.</p>
          <div class="flex flex-wrap gap-2 justify-center mt-4">
            <button id="st-go-cards" class="bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">More flashcards</button>
            <button id="st-go-home" class="bg-white text-sage-600 border border-sage-200 hover:border-sage-300 px-4 py-2 rounded-lg text-sm font-medium">Back to start</button>
          </div>
        </div>`;
      slot.querySelector("#st-go-cards").addEventListener("click", () => navigate("flashcards"));
      slot.querySelector("#st-go-home").addEventListener("click", () => navigate("dashboard"));
    } else {
      btn.disabled = false;
      btn.textContent = "Mark as complete";
      if (msg) msg.textContent = "Couldn't save just now — try again.";
    }
  });
}
